import { useMemo, useState } from "react";
import { Card, CardHeader, CardBody } from "../../design-system/Card";
import { Badge } from "../../design-system/Badge";
import { Icon } from "../../design-system/Icon";
import { calculatePrice, breakdownAtPrice, DEFAULT_CALC_INPUT, type PriceCalcInput, type AdsMode } from "../../lib/pricing-calculator";

/**
 * Calculadora de precio de venta (Desarrollo de Producto). Costea TODO — costo proveedor, comisión de
 * la categoría de ML (editable), cargo fijo, publicidad, embalaje, envío y otros — y despeja el precio
 * de venta para el margen objetivo. Además permite fijar un precio manual y ver el margen que deja.
 * Toda la matemática está en `lib/pricing-calculator` (testeada).
 */
const clp = (v: number): string => `$${Math.round(v).toLocaleString("es-CL")}`;

/** Campo numérico con etiqueta y sufijo (CLP o %). */
function NumField({ label, value, onChange, suffix = "$", hint, min = 0 }: {
  label: string; value: number; onChange: (v: number) => void; suffix?: string; hint?: string; min?: number;
}): JSX.Element {
  return (
    <label className="pd-field">
      <span className="pd-field__label">{label}{hint ? <span className="faint" style={{ fontWeight: 400 }}> · {hint}</span> : null}</span>
      <div className="row" style={{ gap: 6, alignItems: "center" }}>
        {suffix === "$" ? <span className="faint">$</span> : null}
        <input className="pd-input" type="number" inputMode="numeric" min={min} value={Number.isFinite(value) ? value : 0}
          aria-label={label} onChange={(e) => onChange(Number(e.target.value))} style={{ flex: 1 }} />
        {suffix === "%" ? <span className="faint">%</span> : null}
      </div>
    </label>
  );
}

export function PriceCalculator(): JSX.Element {
  const [i, setI] = useState<PriceCalcInput>(DEFAULT_CALC_INPUT);
  const [manualPrice, setManualPrice] = useState<number>(0);
  const set = <K extends keyof PriceCalcInput>(k: K, v: PriceCalcInput[K]): void => setI((p) => ({ ...p, [k]: v }));

  const result = useMemo(() => calculatePrice(i), [i]);
  const manual = useMemo(() => (manualPrice > 0 ? breakdownAtPrice(i, manualPrice) : null), [i, manualPrice]);

  const marginTone = result.marginPct >= (i.targetMarginPct - 1) ? "success" : "warning";

  return (
    <Card style={{ marginTop: "var(--sp-5)" }}>
      <CardHeader
        title={<span className="row" style={{ gap: 8 }}><Icon name="target" size={18} /> Calculadora de precio de venta</span>}
        subtitle="Costea todo (proveedor, comisión ML, cargo fijo, ads, embalaje, envío) y calcula el precio para tu margen objetivo."
        action={<Badge tone="brand" dot>MELI Chile</Badge>}
      />
      <CardBody>
        <div className="pd-form" style={{ gridTemplateColumns: "1fr 1fr" }}>
          <NumField label="Costo proveedor" value={i.providerCost} onChange={(v) => set("providerCost", v)} hint="lo que pagas por unidad" />
          <NumField label="Comisión categoría ML" value={i.commissionPct} onChange={(v) => set("commissionPct", v)} suffix="%" hint="editable · 13–20% en Chile" />
          <NumField label="Cargo fijo ML" value={i.fixedFee} onChange={(v) => set("fixedFee", v)} hint="por unidad, productos baratos" />
          <NumField label="Umbral cargo fijo" value={i.fixedFeeThreshold} onChange={(v) => set("fixedFeeThreshold", v)} hint="aplica bajo este precio" />
          <label className="pd-field">
            <span className="pd-field__label">Publicidad / Ads</span>
            <div className="row" style={{ gap: 6, alignItems: "center" }}>
              <input className="pd-input" type="number" inputMode="numeric" min={0} value={i.ads} aria-label="Publicidad"
                onChange={(e) => set("ads", Number(e.target.value))} style={{ flex: 1 }} />
              <select className="pd-input" value={i.adsMode} aria-label="Modo de publicidad"
                onChange={(e) => set("adsMode", e.target.value as AdsMode)} style={{ width: 92 }}>
                <option value="pct">%</option>
                <option value="amount">$ fijo</option>
              </select>
            </div>
          </label>
          <NumField label="Embalaje" value={i.packaging} onChange={(v) => set("packaging", v)} hint="caja, relleno, etiqueta" />
          <NumField label="Envío que asumes" value={i.shipping} onChange={(v) => set("shipping", v)} hint="0 si lo paga el comprador" />
          <NumField label="Otros costos" value={i.otherCost} onChange={(v) => set("otherCost", v)} hint="mano de obra, etc." />
          <NumField label="Margen objetivo" value={i.targetMarginPct} onChange={(v) => set("targetMarginPct", v)} suffix="%" hint="utilidad sobre el precio" />
          <label className="pd-field" style={{ justifyContent: "flex-end" }}>
            <span className="pd-field__label">IVA 19% sobre tarifas ML</span>
            <label className="row" style={{ gap: 8, cursor: "pointer" }}>
              <input type="checkbox" checked={i.ivaOnFees} onChange={(e) => set("ivaOnFees", e.target.checked)} aria-label="IVA sobre tarifas" />
              <span className="faint" style={{ fontSize: 12 }}>Las comisiones de ML llevan IVA (crédito fiscal si declaras)</span>
            </label>
          </label>
        </div>

        {/* Resultado principal */}
        <div className="callout" style={{ marginTop: "var(--sp-4)" }}>
          <div className="bar" />
          <div className="body" style={{ width: "100%" }}>
            {result.suggestedPrice === null ? (
              <div className="strong" style={{ color: "var(--danger)" }}>{result.warning}</div>
            ) : (
              <>
                <div className="row" style={{ justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
                  <div>
                    <div className="faint" style={{ fontSize: 12 }}>Precio de venta sugerido</div>
                    <div className="tnum" style={{ fontSize: 28, fontWeight: 700 }}>{clp(result.suggestedPrice)}</div>
                  </div>
                  <div className="row" style={{ gap: 16 }}>
                    <div>
                      <div className="faint" style={{ fontSize: 12 }}>Utilidad / unidad</div>
                      <div className="tnum strong" style={{ fontSize: 18 }}>{clp(result.profit)}</div>
                    </div>
                    <div>
                      <div className="faint" style={{ fontSize: 12 }}>Margen real</div>
                      <Badge tone={marginTone} dot>{result.marginPct}%</Badge>
                    </div>
                    <div>
                      <div className="faint" style={{ fontSize: 12 }}>Markup</div>
                      <div className="tnum">{result.markupPct}%</div>
                    </div>
                  </div>
                </div>

                {/* Desglose */}
                <table className="table" style={{ marginTop: "var(--sp-3)" }}>
                  <tbody>
                    {result.lines.map((l) => (
                      <tr key={l.label}>
                        <td style={{ color: l.kind === "profit" ? "var(--success)" : undefined }}>{l.label}</td>
                        <td className="tnum" style={{ textAlign: "right", fontWeight: l.kind === "profit" ? 700 : 400, color: l.kind === "profit" ? "var(--success)" : undefined }}>{clp(l.amount)}</td>
                      </tr>
                    ))}
                    <tr>
                      <td className="strong">Precio de venta</td>
                      <td className="tnum strong" style={{ textAlign: "right" }}>{clp(result.suggestedPrice)}</td>
                    </tr>
                  </tbody>
                </table>
              </>
            )}
          </div>
        </div>

        {/* Modo inverso: precio manual → margen */}
        <div style={{ marginTop: "var(--sp-4)" }}>
          <div className="section-title">¿Y si vendo a un precio fijo?</div>
          <div className="row" style={{ gap: 12, alignItems: "center", marginTop: "var(--sp-2)", flexWrap: "wrap" }}>
            <div style={{ maxWidth: 220 }}><NumField label="Precio de venta a evaluar" value={manualPrice} onChange={setManualPrice} /></div>
            {manual ? (
              <div className="row" style={{ gap: 16 }}>
                <div><div className="faint" style={{ fontSize: 12 }}>Utilidad</div><div className={`tnum strong`} style={{ color: manual.profit >= 0 ? "var(--success)" : "var(--danger)" }}>{clp(manual.profit)}</div></div>
                <div><div className="faint" style={{ fontSize: 12 }}>Margen</div><Badge tone={manual.marginPct >= 0 ? "info" : "danger"} dot>{manual.marginPct.toFixed(1)}%</Badge></div>
                {manual.fixedFeeApplied ? <Badge tone="warning" dot>Aplica cargo fijo</Badge> : null}
              </div>
            ) : <span className="faint" style={{ fontSize: 13 }}>Ingresa un precio para ver el margen que deja.</span>}
          </div>
        </div>

        <div className="faint" style={{ fontSize: 12, marginTop: "var(--sp-3)" }}>
          Los porcentajes y el cargo fijo son <b className="strong">editables</b>: la comisión real de tu categoría confírmala en el simulador oficial de Mercado Libre. Valores por defecto = referencia MELI Chile 2025-2026.
        </div>
      </CardBody>
    </Card>
  );
}
